import { css } from '@emotion/react'
import { Button, Heading, PagedTable, Text, Tooltip } from 'bold-ui'
import { useErrorHandler } from 'components/error'
import { confirm } from 'components/modals/confirm'
import { TableBox, usePagedTableProps } from 'components/table'
import { useInativarImoveisMutation } from 'graphql/hooks.generated'
import { ImoveisQueryInput } from 'graphql/types.generated'
import { Dispatch, Fragment, SetStateAction } from 'react'
import { formatDate } from 'util/date/formatDate'

import { AtualizacaoImoveisProgressBar } from '../../reterritorializacao/AtualizacaoImoveisProgressBar'
import {
  formatAddress,
  getNomesResponsaveisFamiliares,
  getTooltipResponsaveisFamiliares,
} from '../../reterritorializacao/utils/reterritorializacao'
import { InativacaoImoveisQueryResult, InativacaoImovelItemModel } from '../model-inativacaoImoveis'
import { getInativacaoImoveisTopic, imoveisSelecionadosTitle } from '../utils/utils-inativacaoImoveis'
import { InativacaoImoveisCheckAll } from './InativacaoImoveisCheckAll'
import { InativacaoImoveisCheckItem } from './InativacaoImoveisCheckItem'
import { InativacaoImoveisConfirmModal } from './InativacaoImoveisConfirmModal'

interface InativacaoImoveisTableProps {
  pageResult: InativacaoImoveisQueryResult
  loading: boolean
  checkedItems: Set<string>
  setCheckedItems: (items: Set<string>) => void
  setImoveisInput: Dispatch<SetStateAction<ImoveisQueryInput>>
  refetch: () => Promise<unknown>
  progressToken: string
  processingState: boolean
  changeToProcessingState(): void
  resetProcessingState(): void
}

export function InativacaoImoveisTable({
  pageResult,
  loading,
  setImoveisInput,
  refetch,
  checkedItems,
  setCheckedItems,
  progressToken,
  processingState,
  changeToProcessingState,
  resetProcessingState,
}: Readonly<InativacaoImoveisTableProps>) {
  const handleRejection = useErrorHandler()
  const [inativarImoveis, { loading: inativarImoveisLoading }] = useInativarImoveisMutation()
  const tableProps = usePagedTableProps({
    loading,
    result: pageResult,
    onChange: setImoveisInput,
  })
  tableProps.sizeOptions = [10, 30]
  const isDisabled = processingState

  const handleInativarImoveis = () => {
    confirm({
      title: 'Confirmar inativação dos imóveis?',
      body: <InativacaoImoveisConfirmModal numeroImoveis={checkedItems.size} />,
      cancelLabel: 'Cancelar',
      confirmLabel: 'Confirmar',
      onConfirm: () => {
        return inativarImoveis({
          variables: {
            input: {
              imoveisId: [...checkedItems],
              progressToken,
            },
          },
        })
          .then(() => changeToProcessingState())
          .catch((error) => {
            resetProcessingState()
            handleRejection(error)
          })
      },
    })()
  }

  const handleCompleteProcessing = () => {
    void refetch()
    setCheckedItems(new Set())
    resetProcessingState()
  }

  return (
    <Fragment>
      <Heading level={2}>{imoveisSelecionadosTitle(checkedItems.size)}</Heading>
      <TableBox
        header={
          processingState ? (
            <AtualizacaoImoveisProgressBar
              topic={getInativacaoImoveisTopic(progressToken)}
              onCompleteCallBack={handleCompleteProcessing}
              progressToken={progressToken}
              renderProgressBar={processingState}
              progressMessage='Inativando imóveis'
              successMessage='Imóveis inativados com sucesso.'
            />
          ) : undefined
        }
        css={css`
          margin-top: 12px;
        `}
      >
        <PagedTable<InativacaoImovelItemModel>
          {...tableProps}
          columns={[
            {
              name: 'check',
              header: (
                <InativacaoImoveisCheckAll
                  checkedItems={checkedItems}
                  setCheckedItems={setCheckedItems}
                  pageResult={pageResult}
                  disabled={isDisabled || loading || inativarImoveisLoading}
                />
              ),
              render: (row: InativacaoImovelItemModel) => (
                <InativacaoImoveisCheckItem
                  checkedItems={checkedItems}
                  setCheckedItems={setCheckedItems}
                  item={row}
                  disabled={isDisabled || loading || inativarImoveisLoading}
                />
              ),
              sortable: false,
              style: { width: '1.5rem' },
            },
            {
              name: 'bairro',
              header: 'Bairro',
              render: (row: InativacaoImovelItemModel) => (
                <Text style={styles.noWrap}>{handleNullFields(row.bairro).titleCase()}</Text>
              ),
              sortable: true,
              style: { minWidth: '9rem' },
            },
            {
              name: 'logradouro',
              header: 'Logradouro',
              render: (row: InativacaoImovelItemModel) => <Text>{renderLogradouro(row)}</Text>,
              sortable: true,
            },
            {
              name: 'nomeCidadaoResponsavel',
              header: 'Responsáveis familiares',
              render: (row: InativacaoImovelItemModel) => renderResponsaveisFamiliares(row),
              sortable: false,
            },
            {
              name: 'cnes',
              header: 'Unidade de saúde',
              render: (row: InativacaoImovelItemModel) => (
                <Text style={styles.noWrap}>
                  {handleUnidadeSaudeField(row.unidadeSaude.nome, row.unidadeSaude.ativo)}
                </Text>
              ),
              sortable: false,
              style: { minWidth: '13rem' },
            },
            {
              name: 'ine',
              header: 'Equipe',
              render: (row: InativacaoImovelItemModel) => (
                <Text style={styles.noWrap}>
                  {handleEquipeField(row.equipe?.nome, row.equipe?.ine, row.equipe?.ativo)}
                </Text>
              ),
              sortable: false,
              style: { minWidth: '12rem' },
            },
            {
              name: 'microarea',
              header: 'Microárea',
              render: (row: InativacaoImovelItemModel) => <Text>{handleNullFields(row.microarea)}</Text>,
              sortable: false,
            },
            {
              name: 'dataUltimaAtualizacao',
              header: 'Última atualização',
              render: (row: InativacaoImovelItemModel) => <Text>{formatDate(row.dataUltimaAtualizacao)}</Text>,
              sortable: true,
            },
          ]}
        />
      </TableBox>
      <div css={styles.footer}>
        <Button
          kind='primary'
          size='small'
          disabled={isDisabled || loading || inativarImoveisLoading || checkedItems.size === 0}
          onClick={handleInativarImoveis}
        >
          Inativar imóveis
        </Button>
      </div>
    </Fragment>
  )
}

const renderResponsaveisFamiliares = ({ familias }: InativacaoImovelItemModel) => (
  <Tooltip
    style={css`
      flex-wrap: wrap;
      width: 15rem;
    `}
    text={getTooltipResponsaveisFamiliares(familias)}
    placement='bottom-end'
  >
    <Text>
      {getNomesResponsaveisFamiliares(familias).map((responsavel) => (
        <Text component='p' key={responsavel}>
          {responsavel}
        </Text>
      ))}
    </Text>
  </Tooltip>
)

const renderLogradouro = ({ tipoLogradouro, logradouro, numero, complemento, cep }: InativacaoImovelItemModel) =>
  logradouro ? formatAddress(tipoLogradouro?.nome, logradouro, numero, complemento, cep) : '-'

const handleNullFields = (value: string): string => value ?? '-'

const handleUnidadeSaudeField = (nomeUnidadeSaude: string, isUnidadeSaudeAtiva: boolean) => {
  if (!nomeUnidadeSaude) return '-'

  const status = isUnidadeSaudeAtiva === false ? ' (Inativa)' : ''
  return `${nomeUnidadeSaude}${status}`
}

const handleEquipeField = (equipeNome: string, ine: string, isEquipeAtiva: boolean): string => {
  if (!equipeNome) return '-'

  const status = isEquipeAtiva === false ? ' (Inativa)' : ''
  return `${equipeNome} - ${ine}${status}`
}

const styles = {
  footer: css`
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  `,
  noWrap: css`
    white-space: nowrap;
  `,
}
